import { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate, Link, Navigate, useLocation } from "react-router-dom";
import { WelockLogo } from "@/components/WelockMark";
import { DocBanner } from "@/docs/DocBanner";
import { getNav, getPrevNext, PAGE_MAP, SEARCH_INDEX } from "@/docs/content";
import {
  Search, Menu, X, ArrowLeft, ArrowRight, Twitter, Github, Send, MessageCircle,
  ChevronRight, Hash, ExternalLink,
} from "lucide-react";

const SOCIALS = [
  { icon: Twitter, href: "https://x.com", label: "X" },
  { icon: Github, href: "https://github.com", label: "GitHub" },
  { icon: MessageCircle, href: "https://discord.com", label: "Discord" },
  { icon: Send, href: "https://telegram.org", label: "Telegram" },
];

function SearchBox() {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const boxRef = useRef(null);

  const results = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return [];
    return SEARCH_INDEX.filter((r) => r.hay.includes(t)).slice(0, 8);
  }, [q]);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const go = (r) => {
    setOpen(false);
    setQ("");
    navigate(`/docs/${r.slug}${r.hash ? `#${r.hash}` : ""}`);
  };

  return (
    <div ref={boxRef} className="relative w-full max-w-md">
      <div className="flex items-center gap-2 border border-welock-border bg-welock-bg px-3 py-2">
        <Search size={14} className="text-welock-textMuted" />
        <input
          data-testid="docs-search-input"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => { if (e.key === "Enter" && results[0]) go(results[0]); if (e.key === "Escape") setOpen(false); }}
          placeholder="Search docs"
          className="w-full bg-transparent font-mono text-xs text-welock-textPrimary outline-none placeholder:text-welock-textMuted"
        />
        <span className="hidden border border-welock-border px-1.5 py-0.5 font-mono text-[9px] text-welock-textMuted sm:block">/</span>
      </div>
      {open && q && (
        <div data-testid="docs-search-results" className="absolute left-0 right-0 top-full z-50 mt-2 max-h-80 overflow-y-auto border border-welock-border bg-welock-surface">
          {results.length === 0 ? (
            <div className="px-4 py-4 font-mono text-xs text-welock-textMuted">No results for "{q}"</div>
          ) : (
            results.map((r, i) => (
              <button
                key={i}
                data-testid={`search-result-${r.slug}${r.hash ? "-" + r.hash : ""}`}
                onClick={() => go(r)}
                className="flex w-full items-center gap-3 border-b border-welock-border/60 px-4 py-2.5 text-left last:border-0 hover:bg-welock-surfaceHover"
              >
                <Hash size={13} className="shrink-0 text-welock-textMuted" />
                <span className="min-w-0">
                  <span className="block truncate text-sm text-welock-textPrimary">{r.title}</span>
                  <span className="block truncate font-mono text-[10px] text-welock-textMuted">{r.section}</span>
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function Sidebar({ slug, onNavigate }) {
  const nav = getNav();
  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-welock-border px-5 py-4">
        <Link to="/" data-testid="docs-home-link"><WelockLogo /></Link>
        <div className="mt-1 pl-9 font-mono text-[10px] uppercase tracking-widest text-welock-textMuted">Documentation</div>
      </div>
      <nav className="flex-1 space-y-5 overflow-hidden px-4 py-5">
        {nav.map((group) => (
          <div key={group.section}>
            <div className="px-2 font-mono text-[10px] uppercase tracking-widest text-welock-textMuted">{group.section}</div>
            <div className="mt-1.5 space-y-0.5">
              {group.pages.map((p) => {
                const active = p.slug === slug;
                return (
                  <Link
                    key={p.slug}
                    to={`/docs/${p.slug}`}
                    onClick={onNavigate}
                    data-testid={`sidebar-${p.slug}`}
                    className={`block border-l-2 py-1.5 pl-3 font-mono text-[13px] transition-colors ${
                      active
                        ? "border-welock-accent bg-welock-surface text-welock-textPrimary"
                        : "border-transparent text-welock-textSecondary hover:text-welock-textPrimary"
                    }`}
                  >
                    {p.title}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>
    </div>
  );
}

function useActiveHeading(ids) {
  const [active, setActive] = useState(ids[0]);
  useEffect(() => {
    if (!ids.length) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: "-25% 0px -65% 0px", threshold: 0 }
    );
    ids.forEach((id) => { const el = document.getElementById(id); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, [ids.join(",")]);
  return active;
}

export default function Docs() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawer, setDrawer] = useState(false);
  const page = PAGE_MAP[slug];

  const ids = useMemo(() => (page ? page.headings.map((h) => h.id) : []), [page]);
  const active = useActiveHeading(ids);

  useEffect(() => { setDrawer(false); }, [slug]);
  useEffect(() => {
    if (!page) return;
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) { setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 60); return; }
    }
    window.scrollTo(0, 0);
  }, [slug, location.hash, location.key]); // eslint-disable-line

  if (!page) return <Navigate to="/docs/introduction" replace />;

  const { prev, next } = getPrevNext(slug);
  const Content = page.Content;

  return (
    <div className="min-h-screen bg-welock-bg text-welock-textPrimary">
      {/* Fixed sidebar */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 border-r border-welock-border bg-welock-bg lg:block">
        <Sidebar slug={slug} />
      </aside>

      {/* Mobile drawer */}
      {drawer && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setDrawer(false)} />
          <div className="absolute inset-y-0 left-0 w-72 border-r border-welock-border bg-welock-bg" data-testid="docs-drawer">
            <div className="flex justify-end p-3">
              <button onClick={() => setDrawer(false)} className="flex h-8 w-8 items-center justify-center border border-welock-border"><X size={15} /></button>
            </div>
            <Sidebar slug={slug} onNavigate={() => setDrawer(false)} />
          </div>
        </div>
      )}

      {/* Top bar */}
      <header className="fixed inset-x-0 top-0 z-30 border-b border-welock-border bg-welock-bg/90 backdrop-blur-md lg:left-64">
        <div className="flex items-center gap-3 px-4 py-3 md:px-8">
          <button data-testid="docs-menu" onClick={() => setDrawer(true)} className="flex h-9 w-9 items-center justify-center border border-welock-border lg:hidden">
            <Menu size={16} />
          </button>
          <SearchBox />
          <div className="ml-auto flex items-center gap-1">
            {SOCIALS.map((s) => (
              <a key={s.label} href={s.href} target="_blank" rel="noreferrer" data-testid={`social-${s.label.toLowerCase()}`} aria-label={s.label}
                className="flex h-9 w-9 items-center justify-center border border-transparent text-welock-textMuted transition-colors hover:border-welock-border hover:text-welock-textPrimary">
                <s.icon size={15} />
              </a>
            ))}
          </div>
        </div>
      </header>

      {/* Main */}
      <div className="lg:pl-64">
        <div className="flex max-w-5xl gap-10 px-5 pt-24 md:px-0 md:pl-10 lg:pl-14 xl:max-w-6xl 2xl:mx-auto 2xl:max-w-[1440px] 2xl:pl-20">
          {/* content, centre left */}
          <main className="min-w-0 max-w-3xl flex-1 pb-24">
            <DocBanner eyebrow={page.eyebrow} title={page.title} motif={page.motif} />
            <Content />

            {/* prev / next */}
            <div className="mt-16 grid gap-3 border-t border-welock-border pt-8 sm:grid-cols-2">
              {prev ? (
                <Link to={`/docs/${prev.slug}`} data-testid="doc-prev" className="group flex flex-col border border-welock-border bg-welock-surface p-4 transition-colors hover:border-welock-hairline">
                  <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-welock-textMuted"><ArrowLeft size={12} /> Previous</span>
                  <span className="mt-1 font-heading text-sm text-welock-textPrimary group-hover:text-welock-accent">{prev.title}</span>
                </Link>
              ) : <div />}
              {next ? (
                <Link to={`/docs/${next.slug}`} data-testid="doc-next" className="group flex flex-col items-end border border-welock-border bg-welock-surface p-4 text-right transition-colors hover:border-welock-hairline">
                  <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-welock-textMuted">Next <ArrowRight size={12} /></span>
                  <span className="mt-1 font-heading text-sm text-welock-textPrimary group-hover:text-welock-accent">{next.title}</span>
                </Link>
              ) : <div />}
            </div>
          </main>

          {/* On this page */}
          <aside className="sticky top-24 hidden h-max w-52 shrink-0 lg:block">
            <div className="font-mono text-[10px] uppercase tracking-widest text-welock-textMuted">On this page</div>
            <ul className="mt-3 space-y-1.5 border-l border-welock-border">
              {page.headings.map((h) => (
                <li key={h.id}>
                  <a
                    href={`#${h.id}`}
                    data-testid={`toc-${h.id}`}
                    className={`-ml-px block border-l-2 pl-3 font-mono text-[12px] transition-colors ${
                      active === h.id ? "border-welock-accent text-welock-accent" : "border-transparent text-welock-textMuted hover:text-welock-textPrimary"
                    }`}
                  >
                    {h.label}
                  </a>
                </li>
              ))}
            </ul>
            <a href="/" className="mt-6 flex items-center gap-1.5 border-t border-welock-border pt-4 font-mono text-[11px] text-welock-textMuted hover:text-welock-accent">
              Back to welock <ExternalLink size={11} />
            </a>
          </aside>
        </div>
      </div>
    </div>
  );
}
